import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppLayout from '../../../component/appLayout';
import {
  BoxWrapper,
  Content,
  ContentHeading,
  Header,
  HeaderLeft,
  HeaderTitle,
  LeftContent,
  TableSection
} from '../styles';
import { BottomData, Table } from '../styles/styles';
import { getCustomerList } from '../../../services/customerServices';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { CustomerDetailsInterface } from '../../../interfaces/CustomerInterface';
import { getProjectReportData } from '../../../services/reportService';
import { addInvitedMember, removeInvitedMember } from '../../../services/projectServices';
import { ProjectReportInterface } from '../../../interfaces/ReportInterface';
import { captureException } from '../../../services/logService';
import { useHistory } from 'react-router-dom';
import { Dropdown, DropdownItem } from '../../../component/dropdown';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { useMobileDevice } from '../../../global/useMobile';
import ResponsiveHeader from '../../../component/responsiveHeader';
import BottomSubNavigation from '../../../component/bottomNavigation/subNavigation';
import Chip from '../../../component/chipComponent';
import { CHIP_ICON_POSITION, CHIP_SIZE, CHIP_TYPE, EMPTY_STATE_SIZE } from '../../../global/constants';
import { isEmpty } from '../../../helpers/common';
import EmptyStateV2 from '../../../component/emptyState';
import dayjs from 'dayjs';
import {ROUTES} from "../../../routes/urls";

const Projects: React.FC = () => {
  //States
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [searchCustomer, setSearchCustomer] = useState('');
  const [selectAllCustomer, setSelectAllCustomer] = useState(true);
  const [customerList, setCustomerList] = useState<CustomerDetailsInterface[]>([]);
  const [filteredProjectReportData, setFilteredProjectReportData] = useState<ProjectReportInterface[]>([]);

  //Mobx store variables
  const { reportStore, workspaceStore, currentUser } = rootStore;
  const { projectReportData, loading } = reportStore;
  const { currentWorkspace: workspace } = workspaceStore;

  // Other variables
  const history = useHistory();
  const isMobile = useMobileDevice();

  /**
   * Filters project report data based on selected customers
   * @param data - Array of project report data to filter
   * @param customers - Array of customer details to filter by
   * @param isCheckedAll - Boolean indicating if all customers are selected
   */
  const applyFilter = useCallback(
    (data: ProjectReportInterface[], customers: CustomerDetailsInterface[], isCheckedAll = false) => {
      if (isCheckedAll) {
        setFilteredProjectReportData(data);
      } else {
        let filteredData = JSON.parse(JSON.stringify(data));
        const filteredCustomers: string[] = customers?.filter((item) => item?.checked)?.map((x) => x?.id) || [];
        filteredData = filteredData.filter((item: ProjectReportInterface) =>
          filteredCustomers.includes(item?.customerId)
        );
        setFilteredProjectReportData(filteredData);
      }
    },
    []
  );

  /**
   * Loads project report data and customer list for the current workspace
   * Fetches data from API and initializes the customer list with all items checked
   */
  const loadData = useCallback(async () => {
    try {
      if (!isEmpty(workspace?.id)) {
        const result = await Promise.all([getProjectReportData(workspace?.id), getCustomerList()]);
        let customerResponse = result[1];
        customerResponse = customerResponse?.map((item: CustomerDetailsInterface) => {
          return { ...item, checked: true };
        });
        setCustomerList(customerResponse);
        applyFilter(result[0], customerResponse, true);
      }
    } catch (error) {
      captureException(error);
    }
  }, [applyFilter, workspace?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Handles click event on client dropdown
   * @param value - Boolean indicating if dropdown should be open
   */
  const onClickClient = useCallback((value: boolean) => {
    setIsCustomerDropdownOpen(value);
  }, []);

  /**
   * Handles select all customers functionality
   * Updates all customers' checked state and applies filter accordingly
   * @param customerList - Array of customer details
   * @param value - Boolean indicating if all customers should be selected
   */
  const onSelectAllCustomerClick = useCallback(
    (customerList: CustomerDetailsInterface[], value: boolean) => {
      setSelectAllCustomer(value);
      let customerListClone: CustomerDetailsInterface[] = JSON.parse(JSON.stringify(customerList || []));
      customerListClone = customerListClone?.map((item) => {
        return { ...item, checked: value };
      });
      setCustomerList(customerListClone);
      setIsCustomerDropdownOpen(false);
      applyFilter(projectReportData, customerListClone, !value);
    },
    [applyFilter, projectReportData]
  );

  /**
   * Updates search input value for customer filtering
   * @param value - Search string to filter customers
   */
  const onChangeSearchCustomer = useCallback((value: string) => {
    setSearchCustomer(value);
  }, []);

  /**
   * Handles individual customer checkbox selection
   * Updates customer checked state and applies filter
   * @param allUserList - Complete list of customers
   * @param filteredList - Currently filtered list of customers
   * @param userId - ID of the customer being checked/unchecked
   * @param value - Boolean indicating if customer should be checked
   */
  const onCheckCustomer = useCallback(
    (
      allUserList: CustomerDetailsInterface[],
      filteredList: CustomerDetailsInterface[],
      userId: string,
      value: boolean
    ) => {
      const updatedAllList = allUserList?.map((item) => {
        return item?.id === userId ? { ...item, checked: value } : item;
      }, []);
      const updatedFilteredList = filteredList?.map((item) => {
        return item?.id === userId ? { ...item, checked: value } : item;
      }, []);
      const uncheckedTasks = updatedFilteredList?.find((x) => !x?.checked);
      const isAllSelected = updatedAllList?.filter((x) => x?.checked);
      if (uncheckedTasks || updatedFilteredList?.length === 0) setSelectAllCustomer(false);
      else setSelectAllCustomer(true);
      setCustomerList(updatedAllList);
      setIsCustomerDropdownOpen(false);
      applyFilter(projectReportData, updatedAllList, isAllSelected?.length === 0);
    },
    [applyFilter, projectReportData]
  );

  /**
   * Handles join project action
   * @param project - Project details to join
   */
  const onClickJoinAction = useCallback(
    async (project: ProjectReportInterface) => {
      try {
        rootStore.reportStore.setUserExist(project?.id, true);
        const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
        const payload = {
          userId: userDetails?.id,
          newUserId: userDetails?.id,
          companyId: workspace?.id,
          projectId: project?.id
        };
        const result = await addInvitedMember(payload, true, true);
        if (result) {
          const updatedFilteredProjectReportData = filteredProjectReportData?.map((item) => {
            return item?.id === project?.id ? { ...item, isUserExist: true } : item;
          });
          setFilteredProjectReportData(updatedFilteredProjectReportData);
        }
      } catch (error) {
        captureException(error);
      }
    },
    [filteredProjectReportData, workspace?.id]
  );

  /**
   * Handles leave project action
   * @param project - Project details to leave
   */
  const onClickLeaveAction = useCallback(
    async (project: ProjectReportInterface) => {
      try {
        rootStore.reportStore.setUserExist(project?.id, false);
        const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
        const result = await removeInvitedMember(userDetails?.id, project?.id, userDetails?.id, true, true);
        if (result) {
          const updatedFilteredProjectReportData = filteredProjectReportData?.map((item) => {
            return item?.id === project?.id ? { ...item, isUserExist: false } : item;
          });
          setFilteredProjectReportData(updatedFilteredProjectReportData);
        }
      } catch (error) {
        captureException(error);
      }
    },
    [filteredProjectReportData]
  );

  /**
   * Navigates to project details if user is a member, shows error otherwise
   * @param item - Project details that was clicked
   */
  const handleClick = useCallback(
    (item: ProjectReportInterface) => {
      if (item?.isUserExist) {
        rootStore.projectStore.setCurrentProject(item?.id);
        history.push(ROUTES.PROJECT_TASKS.replace(':id', item?.id));
      } else {
        rootStore.alertNotificationStore.setErrorMessage('You have to join this project first!');
      }
    },
    [history]
  );

  /**
   * Renders customer dropdown data with search and select all functionality
   * @returns JSX element containing customer list with checkboxes
   */
  const renderCustomerData = useMemo(() => {
    const filteredCustomerList = isEmpty(searchCustomer)
      ? customerList
      : customerList?.filter((item: CustomerDetailsInterface) =>
          item?.companyName?.toLowerCase().includes(searchCustomer?.toLowerCase())
        ) || [];

    return (
      <BottomData>
        {isEmpty(searchCustomer) && (
          <DropdownItem
            label='Select All'
            isCheckBox={true}
            isChecked={selectAllCustomer}
            onClick={() => onSelectAllCustomerClick(filteredCustomerList, !selectAllCustomer)}
          />
        )}
        {filteredCustomerList?.map((item) => {
          return (
            <DropdownItem
              key={item?.id}
              label={item?.companyName}
              isCheckBox={true}
              isChecked={item?.checked}
              onClick={() => onCheckCustomer(customerList, filteredCustomerList, item?.id, !item?.checked)}
            />
          );
        })}
      </BottomData>
    );
  }, [customerList, onCheckCustomer, onSelectAllCustomerClick, searchCustomer, selectAllCustomer]);

  /**
   * Renders project data table or empty state
   * @returns JSX element containing project table or empty state
   */
  const renderProjectData = useMemo(() => {
    if ((filteredProjectReportData?.length === 0 || !filteredProjectReportData) && !loading) {
      return (
        <EmptyStateV2
          size={EMPTY_STATE_SIZE.MEDIUM}
          title={'There is no report data.'}
          description={'You have no report data.'}
          imageUrl={'reports_empty'}
          isAlignedCenter={true}
        />
      );
    }
    if (loading) {
      return;
    }
    return (
      <TableSection>
        <Table isProject>
          <thead>
            <tr>
              <th className='first-column'>Projects</th>
              <th className='second-column'>Client Company</th>
              <th className='third-column'>Started</th>
              <th className='forth-column'>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredProjectReportData?.map((item) => {
              return (
                <tr key={item?.id}>
                  <td role='button' onClick={() => handleClick(item)}>
                    {item?.projectName}
                  </td>
                  <td>{item?.customerName}</td>
                  <td>{dayjs(item?.startedDate).format(currentUser?.dateFormat)}</td>
                  <td>
                    {item?.isUserExist ? (
                      <div role='button' onClick={() => onClickLeaveAction(item)} className='leave-link'>
                        Leave
                      </div>
                    ) : (
                      <div role='button' onClick={() => onClickJoinAction(item)} className='join-link'>
                        Join
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableSection>
    );
  }, [filteredProjectReportData, handleClick, loading, onClickJoinAction, onClickLeaveAction, currentUser?.dateFormat]);

  const renderFilterOptions = useMemo(() => {
    return (
      <ContentHeading>
        <LeftContent>
          <Dropdown
            modalTitle='Client'
            onChangeSearch={onChangeSearchCustomer}
            position='right'
            content={
              <Chip
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                title='Client'
                trailingIconName='down-arrow-icon'
                trailingIconClassName={isCustomerDropdownOpen ? 'dropdown-icon ' : ''}
                onClick={() => onClickClient(!isCustomerDropdownOpen)}
                isActive={isCustomerDropdownOpen}
              />
            }
            trigger='click'
            className='third-child'
            onOutsideClick={() => setIsCustomerDropdownOpen(false)}>
            {renderCustomerData}
          </Dropdown>
        </LeftContent>
      </ContentHeading>
    );
  }, [isCustomerDropdownOpen, onChangeSearchCustomer, onClickClient, renderCustomerData]);

  return (
    <>
      <AppLayout>
        <BoxWrapper>
          {!isMobile ? (
            <Header>
              <HeaderLeft>
                <HeaderTitle>Projects</HeaderTitle>
              </HeaderLeft>
              {renderFilterOptions}
            </Header>
          ) : (
            <ResponsiveHeader title='Projects' noMargin={true} />
          )}
          <Content>
            {isMobile && renderFilterOptions}
            {renderProjectData}
          </Content>
        </BoxWrapper>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
};

export default observer(Projects);
